<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounce } from '@vueuse/core';
import type { FieldSchema } from '../../../types';
import { applyShelterCurrency, formatMoney, toCents } from '@Helpers';
import { normalizeAmount } from './amountSanitizer.helper';

export interface Model {
  value: string | number;
  type: string;
}

const props = defineProps<{
  disabled?: boolean;
  error?: string;
  field?: FieldSchema;
  placeholder?: string;
  required?: boolean;
}>();

const { t } = useI18n();

const model = defineModel<Model | undefined>();
const modelCost = ref();
const modelType = ref();
const isInternalUpdate = ref(false);

const doses = computed(() => props.field?.settings?.amount?.doses);
const amount = computed(() => props.field?.settings?.amount?.amount);
const amountUnit = computed(() => props.field?.settings?.amount?.amount_unit);
const parsedAmount = computed(() => normalizeAmount(amount.value));
const options = computed(() => [
  { label: `${t('units.perUnit')}`, value: 'Per Unit' },
  { label: `${t('units.perMedication')}`, value: 'Per Medication' },
]);

const totalCost = computed(() => {
  if (!modelCost.value) return 0;

  const calculation = (toCents(modelCost.value) * (parsedAmount.value || 1) * doses.value) / 100;
  return calculation.toFixed(2);
});

const input = useDebounce(modelCost, 400);

onMounted(() => setModelsOnStart());

watch([input, modelType, amount, amountUnit, doses], () => updateModel());
watch([doses, amount, amountUnit], () => checkReverseCalculations());
watch(
  () => props.field?.model,
  () => internalUpdate()
);

function internalUpdate() {
  if (isInternalUpdate.value) return;

  setModelsOnStart();
}

function setModelsOnStart() {
  const hasInvalidModel = !model.value || model.value.value == null || !model.value.type;
  if (hasInvalidModel) {
    modelCost.value = null;
    modelType.value = null;
    model.value = undefined;
    return;
  }

  const hasReverseCalcAndIsPerUnit = props.field?.settings?.reverseCalculations && model.value?.type === 'Per Unit';

  modelCost.value = hasReverseCalcAndIsPerUnit ? reverseTotalCalculation() : Number(model.value?.value || 0);
  modelType.value = model.value?.type || null;
}

function checkReverseCalculations() {
  const hasReverseCalcAndIsPerUnit =
    props.field?.settings?.reverseCalculations && model.value?.type === 'Per Unit' && model.value?.value != null;
  if (!hasReverseCalcAndIsPerUnit) return;

  modelCost.value = reverseTotalCalculation();
}

function reverseTotalCalculation() {
  if (model.value?.value === 0) return model.value?.value;

  const calculation = toCents(Number(model.value?.value)) / (parsedAmount.value || 1) / doses.value / 100;
  return calculation.toFixed(2);
}

function updateModel() {
  if (modelCost.value == null || !modelType.value) {
    isInternalUpdate.value = true;

    model.value = undefined;
    nextTick(() => {
      isInternalUpdate.value = false;
    });
    return;
  }

  isInternalUpdate.value = true;

  const cost = modelType.value === 'Per Unit' ? totalCost.value : modelCost.value;

  model.value = { value: Number(cost), type: modelType.value };
  nextTick(() => {
    isInternalUpdate.value = false;
  });
}
</script>

<template>
  <div class="flex flex-col gap-3 sm:flex-row">
    <div class="w-full sm:!w-1/2">
      <AFieldMoney
        v-model="modelCost"
        :error
        :required
        :disabled="field?.state?.disabled || disabled"
      />
    </div>

    <AFieldDropdown
      v-model="modelType"
      class="w-full sm:!w-1/2"
      optionLabel="label"
      optionValue="value"
      :error
      :options
      :placeholder
      :required
      :disabled="field?.state?.disabled || disabled"
      :showClear="!required"
      @change="updateModel"
    />
  </div>

  <div v-if="modelType === 'Per Unit' && modelCost != null">
    <p class="pt-2 text-sm font-medium text-slate-700">
      {{ $t('common.total') }}

      <span class="text-slate-500">
        ({{ modelCost != null ? applyShelterCurrency(formatMoney(modelCost)) : '' }} x {{ amount || '1' }}
        {{ amountUnit || $t('units.unit') }} x {{ doses }} {{ $t('units.dose', doses) }}):
      </span>

      {{ modelCost != null ? applyShelterCurrency(formatMoney(totalCost || modelCost)) : '' }}
    </p>
  </div>
</template>
